import React, { useState, useEffect, useRef, useCallback } from "react";
import { Tooltip, IconButton, Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import { getRolesData, rolesStatusChange } from "../../api/rolesApi/rolesApi";
import { RolesModel, RolesModelInfo } from "../../types/RolesModel";
import { getExcelData } from "../../api/groupsApi/groupsApi";
import { ExcelExportPayload } from "../../types/GroupsModel";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { Col, Row } from "react-bootstrap";
import "./Roles.css";
import "./../../styles/userManagmentCommon.css";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { useSnackbar } from "../Common/SnackbarContext";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface RolesTableProps {
  businessUnitData: any;
}

const RolesTable: React.FC<RolesTableProps> = ({ businessUnitData }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [rowData, setRowData] = useState<RolesModelInfo[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>(rowData);
  const gridRef = useRef<AgGridReact>(null);
  const { setLoading } = useLoader();
  const navigation = useNavigate();
  const { showSnackbar } = useSnackbar();

  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
  };

  const Actions = (props: any) => {
    if (props.data === undefined) {
      return "";
    }
    const handleEditAction = () => {
      const selectedRowData = props.data;
      navigation("/rolesEditAction", {
        state: { rowData: selectedRowData, action: "Edit" },
      });
    };

    const handleDuplicateAction = () => {
      const selectedRowData = props.data;
      navigation("/rolesEditAction", {
        state: { rowData: selectedRowData, action: "Duplicate" },
      });
    };

    const handleStatusChange = async () => {
      const selectedRowData = props.data;
      const payload = {
        ContextName: LocalStorageUtil.getItem("contextName"),
        RoleDataModelObj: {
          RoleId: selectedRowData?.id,
        },
      };
      try {
        await rolesStatusChange(payload);
        getData(rolesDataModel);
      } catch (error) {
        console.log("error", error);
      }
    };
    return (
      <>
        {isPagePermission("Allow Edit","Roles",604) && (
          <Tooltip title="Click to edit">
            <EditIcon
              className="icon-button-other"
              onClick={handleEditAction}
              style={{ padding: "0.2vh" }}
            />
          </Tooltip>
        )}
        {isPagePermission("Allow Duplicate","Roles",605) && (
          <Tooltip title="Click to duplicate">
            <CopyAllIcon
              className="icon-button-other"
              onClick={handleDuplicateAction}
              style={{ padding: "0.2vh", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        )}
        {props.data.active ? (
          <Tooltip title="Click to deactivate the roles">
            <PlayCircleIcon
              className="icon-button-other"
              style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
              onClick={handleStatusChange}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to activate the roles">
            <DoDisturbIcon
              className="icon-button-other"
              onClick={handleStatusChange}
              style={{ padding: "0.2vh", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "Role Name",
      field: "roleName",
      tooltipField: "roleName",
      width: 250,
      colId: "roleName",
      flex: 1,
    },
    {
      headerName: "Customer",
      field: "accountName",
      tooltipField: "accountName",
      width: 250,
      colId: "accountName",
    },
    {
      headerName: "Permissions",
      field: "permissionName",
      tooltipField: "permissionName",
      width: 870,
      colId: "permissionName",
      flex: 1,
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: Actions,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 120,
      minWidth: 120,
      pinned: "right",
    },
  ]);

  const handleCloseSearch = async () => {
    setSearchTerm("");
    setShowEmptySearchMessage(true);
    await getData(rolesDataModel);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      setShowEmptySearchMessage(false);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let updatedRoleModel: RolesModel = {
      ...rolesDataModel,
      SearchText: searchTerm,
    };
    await getData(updatedRoleModel);
    setShowEmptySearchMessage(false);
  };

  let rolesDataModel: RolesModel = {
    AccountId: Number(LocalStorageUtil.getItem("accountId")),
    IsSuperAdmin: 1,
    SearchText: "",
    currentPage: 1,
    isexporttpExcel: false,
    noOfRecords: 25,
    pagename: "Roles",
    sortOrder: 0,
  };

  const getData = async (updatedRoleModel: RolesModel) => {
    setLoading(true);
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
      RoleDataModelObj: updatedRoleModel,
    };
    try {
      const rolesData = await getRolesData(payload);
      const transformedData = rolesData?.roleDataModel?.map((roles: any) => ({
        id: roles.roleId,
        roleName: roles.roleName,
        accountName: roles.accountName,
        active: roles.active,
        createdDate: roles.createdDate,
        permissionName: roles.permission[0].permissionName,
        roles: roles?.role?.map((role: any) => role.roleName).join(", "),
      }));
      setRowData(transformedData);
      if (rolesData && searchTerm) {
        showSnackbar(rolesData.totalCount + " records found.");
      }
      setFilteredUsers(transformedData);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(rolesDataModel);
  }, []);

  const params: ExcelExportPayload = {
    AccountId: businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: "",
    aliasname: "Roles",
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    isexporttpExcel: true,
    offset: appSettings.timezone,
    pagename: "Roles",
    sortOrder: 0,
  };

  const excelData = async () => {
    try {
      const excelDataResponse = await getExcelData(params);
      showSnackbar("File downloaded successfully!");
      if (!excelDataResponse) {
        showSnackbar("No records Found!");
      }
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "RolesData.xlsx");
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching Excel data", error);
    }
  };

  const handleCreate = () => {
    navigation("/rolesEditAction");
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar
        title="User Management"
        text="Roles"
        screenTitle="Roles"
        menuTitle="Configuration"
      />
      <Row style={{ marginLeft: "1em" }}>
        <Col>
          <Box>
            <div className="searchContainer">
              <div className="searchWrapper">
                <div style={{ display: "none", alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Search By Role Name"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="searchInput"
                    onKeyDown={handleSearchKeyPress}
                  />
                  <button onClick={handleSearchClick} className="searchButton">
                    <SearchIcon style={{ color: "#006ab6" }} />
                  </button>
                  {searchTerm && (
                    <IconButton
                      onClick={handleCloseSearch}
                      style={{ padding: "0.5vh" }}
                    >
                      <CloseIcon style={{ color: "#113c8c" }} />
                    </IconButton>
                  )}
                </div>
                {showEmptySearchMessage && (
                  <span className="errorMessage">
                    Please enter text for search
                  </span>
                )}

                <input
                  type="text"
                  id="filter-text-box"
                  placeholder="Quick Filter..."
                  style={{
                    width: "25em",
                    height: "2.5em",
                    fontSize: "11px",
                    paddingLeft: ".5em",
                    marginBottom: ".1em",
                  }}
                  onInput={onFilterTextBoxChanged}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "end" }}>
                {isPagePermission("Allow Create New","Roles",602) && (
                  <Button
                    variant="contained"
                    size="small"
                    className="button create-new-button"
                    onClick={handleCreate}
                  >
                    Create New
                  </Button>
                )}
                {isPagePermission("Allow Export To Excel","Roles",603) && (
                  <button
                    onClick={excelData}
                    className="excel-button"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <img
                      src={ExcelIcon}
                      alt="Excel Icon"
                      className="excel-icon"
                      style={{ marginTop: ".3em" }}
                    />
                  </button>
                )}
              </div>
            </div>
            <div className="rp-main-inner col-sm-12">
              <div className="queue-table-view clearfix">
                <div
                  className="ag-theme-quartz-dark"
                  style={{
                    height: "72vh",
                    marginTop: ".1vh",
                    width: "99%",
                  }}
                >
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={filteredUsers}
                    pagination={true}
                    paginationPageSize={defaulPageSize}
                    paginationPageSizeSelector={pageSizeSelector}
                    rowGroupPanelShow="always"
                    pivotPanelShow="always"
                    gridOptions={{
                      rowSelection: { 
                        mode : "multiRow",
                        checkboxes: true,
                        headerCheckbox: true,
                        enableClickSelection: false,
                       },  
                    }}
                    defaultColDef={defaultColDef}
                    ref={gridRef}
                    loading={!rowData}
                    loadingOverlayComponent={CustomLoadingOverlay}
                    overlayNoRowsTemplate={
                      '<span class="ag-overlay-loading-center">No rows to show</span>'
                    }
                  />
                </div>
              </div>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default RolesTable;
